"use client";

import * as Tabs from "@radix-ui/react-tabs";
import { Session } from "@supabase/supabase-js";
import cn from "classnames";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import Spinner from "@/components/icons/Spinner";
import { useRouter } from "@/navigation";
import { UserOnboarding } from "@/types";
import { getURL, setLocalUserSettings } from "@/utils/helpers";
import { deleteCookie } from "@/utils/server-helpers";
import {
    updateOnboardingStatus,
    upsertUserSettings
} from "@/utils/supabase-client";

import LanguageSelector from "./LanguageSelector";
import MotivationSelector from "./MotivationSelector";
import ProficiencySelector from "./ProficiencySelector";

interface WelcomeTabsProps {
    session: Session | null;
}

const WelcomeTabs = ({ session }: WelcomeTabsProps) => {
    const [currentTab, setCurrentTab] = useState("step1");
    const [userOnboarding, setUserOnboarding] = useState<UserOnboarding>({});
    const [isLoading, setIsLoading] = useState(false);
    const searchParams = useSearchParams();
    const returnTo = searchParams.get("returnTo");

    const router = useRouter();

    // handle user onboarding change and merge with existing userOnboarding
    const handleUserOnboarding = (newUserOnboarding: UserOnboarding) => {
        setUserOnboarding((prevUserOnboarding) => ({
            ...prevUserOnboarding,
            ...newUserOnboarding
        }));
    };

    useEffect(() => {
        async function finishOnboarding() {
            if (session) {
                // if the user logged in/created an account and got to the last step in the onboarding
                // redirect to /study and save onboarding status
                await updateOnboardingStatus(session, true);
                await upsertUserSettings(userOnboarding);
                setLocalUserSettings(userOnboarding);

                deleteCookie("userOnboarding");

                if (returnTo && returnTo === decodeURIComponent("/#pricing")) {
                    // @ts-ignore
                    router.push(`${getURL()}#pricing`);
                } else {
                    router.push("/study");
                }
            } else {
                setLocalUserSettings(userOnboarding);
                // @ts-ignore
                router.push("/login?view=sign_up");
                setIsLoading(false);
            }
        }

        if (userOnboarding.motivation) {
            setCurrentTab("step3");
        } else if (userOnboarding.current_study_language) {
            setCurrentTab("step2");
        }

        // last step completed
        if (
            userOnboarding.current_study_language &&
            userOnboarding.motivation &&
            userOnboarding.proficiency
        ) {
            setIsLoading(true);

            // set cookie
            document.cookie = `userOnboarding=${JSON.stringify(
                userOnboarding
            )}; path=/; max-age=600`;

            finishOnboarding();
        }
    }, [userOnboarding]);

    return (
        <Tabs.Root
            defaultValue="step1"
            value={currentTab}
            onValueChange={setCurrentTab}
        >
            <Tabs.List className="flex justify-center gap-4 mb-6">
                <Tabs.Trigger
                    disabled={isLoading}
                    value="step1"
                    className={cn(
                        "rounded-full font-semibold tabular-nums w-6 h-6 data-[state=active]:ring-2 ring-offset-1 ring-yellow-500",
                        {
                            "bg-blue-600 text-white":
                                currentTab === "step1" ||
                                currentTab === "step2" ||
                                currentTab === "step3"
                        }
                    )}
                >
                    1
                </Tabs.Trigger>
                <Tabs.Trigger
                    disabled={
                        !userOnboarding.current_study_language || isLoading
                    }
                    value="step2"
                    className={cn(
                        "rounded-full font-semibold tabular-nums w-6 h-6 data-[state=active]:ring-2 ring-offset-1 ring-yellow-500",
                        {
                            "bg-blue-600 text-white":
                                currentTab === "step2" ||
                                currentTab === "step3",
                            "bg-zinc-200": currentTab === "step1"
                        }
                    )}
                >
                    2
                </Tabs.Trigger>
                <Tabs.Trigger
                    disabled={!userOnboarding.motivation || isLoading}
                    value="step3"
                    className={cn(
                        "rounded-full font-semibold tabular-nums w-6 h-6 data-[state=active]:ring-2 ring-offset-1 ring-yellow-500",
                        {
                            "bg-blue-600 text-white": currentTab === "step3",
                            "bg-zinc-200":
                                currentTab === "step1" || currentTab === "step2"
                        }
                    )}
                >
                    3
                </Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="step1">
                <LanguageSelector
                    selected={userOnboarding.current_study_language}
                    onSelect={handleUserOnboarding}
                />
            </Tabs.Content>
            <Tabs.Content value="step2">
                <MotivationSelector
                    selected={userOnboarding.motivation}
                    onSelect={handleUserOnboarding}
                />
            </Tabs.Content>
            <Tabs.Content value="step3">
                {!isLoading ? (
                    <ProficiencySelector
                        selected={userOnboarding.proficiency}
                        onSelect={handleUserOnboarding}
                    />
                ) : (
                    <Spinner />
                )}
            </Tabs.Content>
        </Tabs.Root>
    );
};

export default WelcomeTabs;
